/**
 * CreateRoutePage Component
 *
 * Page for creating a new climbing route at a specific gym.
 * Uses a photos-first 2-tab interface:
 * 1. Photos & Location - Upload photos and place route on map
 * 2. Route Info - Grade, setters, colors, description, styles
 *
 * Supports saving as draft or publishing directly.
 */

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  type CSSProperties,
  type FormEvent,
  type ChangeEvent,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { useGym } from '@/hooks/useGyms';
import { useGymMemberships } from '@/hooks/useGymMemberships';
import { useGymSetters, type SetterInfo } from '@/hooks/useGymSetters';
import { useRouteFormValidation } from '@/hooks/useRouteFormValidation';
import { PhotoUpload, type PhotoPreview } from '@/components/upload/PhotoUpload';
import { MapPlacementStep } from '@/components/route/MapPlacementStep';
import { SetterChipsInput } from '@/components/route/SetterChipsInput';
import {
  V_GRADES,
  ROUTE_STYLE_CATEGORIES,
  getWallSections,
  getGradeIndex,
  type VGrade,
  type RoutePhoto,
  type RouteStyle,
  type Point,
  type GradingSystem,
  type RouteIdentification,
  type RouteSetter,
  type RouteStatus,
  type ColorGrade,
} from '@flash/shared';
import { FONT_GRADES, fontToV, vToFont, type FontGrade } from '@/utils/gradeConversion';

/**
 * Tab section identifiers - 2-tab structure
 */
type SectionId = 'photos-location' | 'route-info';

/**
 * Section status
 */
type SectionStatus = 'complete' | 'incomplete';

/**
 * Get today's date as YYYY-MM-DD string for date input
 */
const getTodayString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Form data state
 */
interface RouteFormData {
  name: string;
  officialGrade: VGrade | null;
  wallSection: string;
  colorGrade: string;
  description: string;
  styles: RouteStyle[];
  mapPosition: Point | null;
  /** Route identification details (hold color, tag color) */
  identification: RouteIdentification;
  /** Date the route was set (defaults to today) */
  setDate: string;
  /** Position on the wall (left/center/right) */
  position: 'left' | 'right' | 'center' | null;
}

/**
 * Tab configuration - photos-first flow
 */
const TABS: { id: SectionId; label: string }[] = [
  { id: 'photos-location', label: 'Photos & Location' },
  { id: 'route-info', label: 'Route Info' },
];

/**
 * Generate a new route document ID
 */
const generateRouteId = (): string => {
  const db = getFirebaseDb();
  const routesRef = collection(db, 'routes');
  return doc(routesRef).id;
};

/**
 * Sort color grades by their associated grade (exact or range min)
 * Colors with 'none' association go to the end, sorted alphabetically
 */
const sortColorGradesByGrade = (colors: ColorGrade[]): ColorGrade[] => {
  return [...colors].sort((a, b) => {
    // Get the grade to sort by (exactGrade or minGrade)
    const getGrade = (c: ColorGrade): VGrade | null => {
      if (c.associationType === 'exact' && c.exactGrade) return c.exactGrade;
      if (c.associationType === 'range' && c.minGrade) return c.minGrade;
      return null;
    };

    const gradeA = getGrade(a);
    const gradeB = getGrade(b);

    // Both have grades - sort by grade index
    if (gradeA && gradeB) {
      return getGradeIndex(gradeA) - getGradeIndex(gradeB);
    }

    // Only one has a grade - put the one with grade first
    if (gradeA && !gradeB) return -1;
    if (!gradeA && gradeB) return 1;

    // Neither has a grade - sort alphabetically by displayName
    return a.displayName.localeCompare(b.displayName);
  });
};

/**
 * CreateRoutePage displays the route creation form with tabbed navigation
 *
 * Features:
 * - Photos-first 2-tab interface
 * - Multi-setter selection
 * - Save as draft or publish
 * - Auto-detection of wall section from map position
 */
export const CreateRoutePage = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const { gym, loading: gymLoading, error: gymError } = useGym(gymId);
  const { getRoleAtGym, loading: membershipsLoading } = useGymMemberships();
  const { setters: availableSetters, loading: settersLoading } = useGymSetters(gymId);

  const loading = authLoading || gymLoading || membershipsLoading;

  // Check if user is a setter or owner at this gym
  const userRole = gymId ? getRoleAtGym(gymId) : null;
  const canCreateRoute = userRole === 'setter' || userRole === 'owner';

  // Active section/tab
  const [activeSection, setActiveSection] = useState<SectionId>('photos-location');

  // Form state
  const [formData, setFormData] = useState<RouteFormData>({
    name: '',
    officialGrade: 'V0',
    wallSection: '',
    colorGrade: '',
    description: '',
    styles: [],
    mapPosition: null,
    identification: {},
    setDate: getTodayString(),
    position: null,
  });
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const [routeId, setRouteId] = useState<string>('');

  // Multi-setter state - pre-select current user
  const [selectedSetters, setSelectedSetters] = useState<RouteSetter[]>([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [showMapPlacement, setShowMapPlacement] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Generate route ID on mount (needed for photo upload path)
  useEffect(() => {
    setRouteId(generateRouteId());
  }, []);

  // Pre-select current user as setter when user data loads
  useEffect(() => {
    if (user && userProfile && selectedSetters.length === 0) {
      setSelectedSetters([
        {
          id: user.uid,
          name: userProfile.displayName ?? user.displayName ?? 'Unknown Setter',
        },
      ]);
    }
  }, [user, userProfile, selectedSetters.length]);

  // Initialize form data with gym defaults when gym loads
  // Note: wallSection and identification.holdColor are NOT auto-selected
  // to require explicit user selection before saving draft
  useEffect(() => {
    if (gym) {
      setFormData((prev) => ({
        ...prev,
        // Set official grade to null for color-only grading
        officialGrade: gym.gradingSystem === 'color-only' ? null : prev.officialGrade,
      }));
    }
  }, [gym]);

  // Check if gym has a floor plan for map placement
  const hasFloorPlan = Boolean(gym?.floorPlan?.imageUrl);

  // Get gym settings with defaults
  const requireRouteName = gym?.routeRequirements?.requireRouteName ?? true;
  const requirePhoto = gym?.routeRequirements?.requirePhoto ?? false;
  const gradingSystem: GradingSystem = gym?.gradingSystem ?? 'v-scale';
  const isColorOnly = gradingSystem === 'color-only';

  /**
   * Validate Photos & Location section
   */
  const validatePhotosLocation = useCallback((): boolean => {
    // Photos are only required if gym setting requires them
    if (requirePhoto && photos.length === 0) return false;
    // Map position is required if gym has floor plan
    if (hasFloorPlan && !formData.mapPosition) return false;
    return true;
  }, [requirePhoto, photos, hasFloorPlan, formData.mapPosition]);

  /**
   * Validate Route Info section using shared hook
   */
  const { validateRouteInfo } = useRouteFormValidation(
    {
      name: formData.name,
      officialGrade: formData.officialGrade,
      identification: formData.identification,
    },
    {
      requireRouteName,
      isColorOnly,
      selectedSetters,
      gym,
    }
  );

  /**
   * Compute section completion status
   */
  const sectionStatus = useMemo((): Record<SectionId, SectionStatus> => {
    return {
      'photos-location': validatePhotosLocation() ? 'complete' : 'incomplete',
      'route-info': validateRouteInfo() ? 'complete' : 'incomplete',
    };
  }, [validatePhotosLocation, validateRouteInfo]);

  /**
   * Check if all required sections are complete for publishing
   */
  const canPublish = useMemo(() => {
    return (
      sectionStatus['photos-location'] === 'complete' && sectionStatus['route-info'] === 'complete'
    );
  }, [sectionStatus]);

  /**
   * Check if draft can be saved (requires wall section, photos only if gym requires them)
   */
  const canSaveDraft = useMemo(() => {
    // Wall section is always required
    if (!formData.wallSection) return false;
    // Photos only required if gym setting requires them
    if (requirePhoto && photos.length === 0) return false;
    return true;
  }, [requirePhoto, photos, formData.wallSection]);

  /**
   * Handle form field changes
   */
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handle photo upload completion
   * Store as PhotoPreview[] to persist across tab switches
   */
  const handlePhotoUploadComplete = (results: { url: string; storagePath: string }[]) => {
    const photoPreviews: PhotoPreview[] = results.map((result, index) => ({
      id: `uploaded_${index}_${Date.now()}`,
      previewUrl: result.url,
      file: null, // Already uploaded
      downloadUrl: result.url,
      storagePath: result.storagePath,
    }));
    setPhotos(photoPreviews);
    setPhotosUploading(false);
  };

  /**
   * Toggle a style on/off
   */
  const handleStyleToggle = (style: RouteStyle) => {
    setFormData((prev) => ({
      ...prev,
      styles: prev.styles.includes(style)
        ? prev.styles.filter((s) => s !== style)
        : [...prev.styles, style],
    }));
  };

  /**
   * Handle map position selection
   */
  const handleMapPositionSelect = (position: Point, wallSection: string | null) => {
    setFormData((prev) => ({
      ...prev,
      mapPosition: position,
      wallSection: wallSection ?? prev.wallSection,
    }));
    setShowMapPlacement(false);
  };

  /**
   * Clear a field from identification object
   */
  const clearIdentificationField = (field: 'holdColor' | 'tagColor') => {
    setFormData((prev) => {
      const { [field]: _, ...rest } = prev.identification;
      return {
        ...prev,
        identification: rest,
      };
    });
  };

  /**
   * Convert SetterInfo to RouteSetter format
   */
  const setterInfoToRouteSetter = (setter: SetterInfo): RouteSetter => ({
    id: setter.id,
    name: setter.name,
  });

  /**
   * Handle setter selection changes
   */
  const handleSetterChange = (setters: { id: string; name: string }[]) => {
    setSelectedSetters(setters as RouteSetter[]);
  };

  /**
   * Create route data object
   */
  const createRouteData = (status: RouteStatus) => {
    // Build identification object - only include fields that have values
    const identification: RouteIdentification = {};
    if (formData.identification.holdColor) {
      identification.holdColor = formData.identification.holdColor;
    }
    if (formData.identification.tagColor) {
      identification.tagColor = formData.identification.tagColor;
    }

    // Validate wallSection exists in gym's current floor plan regions
    const validWallSections = gym ? getWallSections(gym) : [];
    const wallSectionToUse =
      formData.wallSection && validWallSections.includes(formData.wallSection)
        ? formData.wallSection
        : (validWallSections[0] ?? '');

    // Convert PhotoPreview[] to RoutePhoto[] for storage
    const routePhotos: RoutePhoto[] = photos.map((photo, index) => ({
      url: photo.downloadUrl ?? photo.previewUrl,
      thumbnailUrl: photo.downloadUrl ?? photo.previewUrl,
      order: index,
    }));

    return {
      id: routeId,
      gymId: gym!.id,
      name: formData.name.trim(),
      officialGrade: formData.officialGrade,
      colorGrade: formData.colorGrade,
      wallSection: wallSectionToUse,
      description: formData.description.trim() || null,
      styles: formData.styles,
      position: formData.position,
      photos: routePhotos,
      mapPosition: formData.mapPosition,
      // Include identification if any fields are set
      ...(Object.keys(identification).length > 0 && { identification }),
      // Multi-setter support
      setters: selectedSetters,
      // Legacy single setter fields for backward compatibility
      setterId: selectedSetters[0]?.id ?? user!.uid,
      setterName:
        selectedSetters[0]?.name ??
        userProfile?.displayName ??
        user!.displayName ??
        'Unknown Setter',
      // Draft ownership - current user can edit
      assignedSetterId: status === 'draft' ? user!.uid : null,
      setDate: Timestamp.fromDate(new Date(formData.setDate)),
      status,
      retiredAt: null,
      consensusGrade: null,
      gradeSubmissionCount: 0,
      averageRating: null,
      ratingCount: 0,
      totalSends: 0,
      totalAttempts: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  };

  /**
   * Handle save as draft
   */
  const handleSaveDraft = async () => {
    if (photosUploading) {
      setSubmitError('Please wait for photos to finish uploading');
      return;
    }

    if (!canSaveDraft) {
      const errorParts = ['A wall section is required'];
      if (requirePhoto) errorParts.push('at least one photo');
      setSubmitError(`${errorParts.join(' and ')} to save as draft`);
      return;
    }

    if (!user || !gym) {
      setSubmitError('Unable to save draft');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);
      const routeData = createRouteData('draft');

      await setDoc(routeRef, routeData);
      navigate(`/gyms/${gymId}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save draft';
      console.error('Error saving draft:', error);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle form submission (publish)
   */
  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();

    if (photosUploading) {
      setSubmitError('Please wait for photos to finish uploading');
      return;
    }

    if (!canPublish) {
      setSubmitError('Please complete all required fields');
      return;
    }

    if (!user || !gym) {
      setSubmitError('Unable to create route');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const db = getFirebaseDb();
      const routeRef = doc(db, 'routes', routeId);
      const routeData = createRouteData('published');

      await setDoc(routeRef, routeData);
      navigate(`/climb`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create route';
      console.error('Error creating route:', error);
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      </main>
    );
  }

  // Check authentication
  if (!user) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Sign In Required</h1>
          <p style={errorTextStyle}>You must be signed in to create a route.</p>
          <Link to="/login" style={actionLinkStyle}>
            Sign In
          </Link>
        </div>
      </main>
    );
  }

  // Check gym exists
  if (gymError) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Error</h1>
          <p style={errorTextStyle}>{gymError}</p>
        </div>
      </main>
    );
  }

  if (!gym) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Gym Not Found</h1>
          <p style={errorTextStyle}>{"The gym you're looking for doesn't exist."}</p>
        </div>
      </main>
    );
  }

  // Check setter permissions
  if (!canCreateRoute) {
    return (
      <main style={containerStyle}>
        <div style={errorContainerStyle}>
          <h1 style={errorTitleStyle}>Permission Denied</h1>
          <p style={errorTextStyle}>{"You don't have permission to create routes at this gym."}</p>
          <p style={errorSubtextStyle}>Only setters and gym owners can create routes.</p>
        </div>
      </main>
    );
  }

  // Render map placement overlay
  if (showMapPlacement && gym.floorPlan) {
    return (
      <div style={mapPlacementContainerStyle}>
        <MapPlacementStep
          floorPlan={gym.floorPlan}
          colorGrades={gym.colorGrades ?? []}
          selectedColor={formData.colorGrade}
          grade={formData.officialGrade ?? 'New'}
          onPositionSelect={handleMapPositionSelect}
          onCancel={() => setShowMapPlacement(false)}
          initialPosition={formData.mapPosition}
        />
      </div>
    );
  }

  const canSubmitPublish = !isSubmitting && !photosUploading && canPublish;
  const canSubmitDraft = !isSubmitting && !photosUploading && canSaveDraft;

  return (
    <main style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <button onClick={() => navigate(-1)} style={backButtonStyle} aria-label="Back to Gym">
          <span style={backArrowStyle}>&#8592;</span>
        </button>
        <div>
          <h1 style={pageTitleStyle}>Create Route</h1>
          <span style={headerSubtitleStyle}>{gym.name}</span>
        </div>
      </header>

      {/* Tab navigation */}
      <nav style={navStyle}>
        <div style={navContentStyle}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              style={{
                ...navButtonStyle,
                ...(activeSection === tab.id ? navButtonActiveStyle : {}),
              }}
            >
              {tab.label}
              {sectionStatus[tab.id] === 'complete' && (
                <span style={completionDotStyle} title="Section complete" />
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <div style={contentStyle}>
        {/* Photos & Location Section */}
        {activeSection === 'photos-location' && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Photos & Location</h2>
            <p style={sectionDescStyle}>
              Upload photos of the route{requirePhoto ? ' (required)' : ''} and place it on the gym
              map.
            </p>

            <div style={formStyle}>
              {/* Photo Upload */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Route Photos {requirePhoto && <span style={requiredStyle}>*</span>}
                </label>
                <span style={helpTextStyle}>
                  Upload 1-3 photos of the route{!requirePhoto && ' (optional)'}
                </span>
                {routeId && (
                  <PhotoUpload
                    routeId={routeId}
                    initialPhotos={photos}
                    onUploadComplete={handlePhotoUploadComplete}
                    onUploadStart={() => setPhotosUploading(true)}
                    onUploadError={(error) => {
                      setPhotosUploading(false);
                      setSubmitError(error);
                    }}
                    minPhotos={1}
                    maxPhotos={3}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {/* Map Placement */}
              {hasFloorPlan && (
                <div style={fieldStyle}>
                  <label style={labelStyle}>
                    Map Location <span style={requiredStyle}>*</span>
                  </label>
                  <span style={helpTextStyle}>Place this route on the gym floor plan</span>
                  <button
                    type="button"
                    onClick={() => setShowMapPlacement(true)}
                    style={mapPlacementButtonStyle}
                    disabled={isSubmitting}
                  >
                    {formData.mapPosition ? (
                      <>
                        <span style={mapPlacementIconStyle}>&#10003;</span>
                        Location Set - Tap to Change
                      </>
                    ) : (
                      <>
                        <span style={mapPlacementIconStyle}>&#128205;</span>
                        Place on Map
                      </>
                    )}
                  </button>
                  {formData.mapPosition && formData.wallSection && (
                    <span style={mapPositionTextStyle}>Wall Section: {formData.wallSection}</span>
                  )}
                </div>
              )}

              {/* Wall Section dropdown - only show if no floor plan */}
              {!hasFloorPlan && getWallSections(gym).length > 0 && (
                <div style={fieldStyle}>
                  <label htmlFor="wallSection" style={labelStyle}>
                    Wall Section <span style={requiredStyle}>*</span>
                  </label>
                  <select
                    id="wallSection"
                    name="wallSection"
                    value={formData.wallSection}
                    onChange={handleChange}
                    style={selectStyle}
                    disabled={isSubmitting}
                  >
                    <option value="">Select wall section...</option>
                    {getWallSections(gym).map((section) => (
                      <option key={section} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Route Info Section */}
        {activeSection === 'route-info' && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Route Information</h2>
            <p style={sectionDescStyle}>Set the grade, setters, and appearance details.</p>

            <div style={formStyle}>
              {/* Route Name - only shown when gym requires route names */}
              {requireRouteName && (
                <div style={fieldStyle}>
                  <label htmlFor="name" style={labelStyle}>
                    Route Name <span style={requiredStyle}>*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., The Crimpy One, Route #42"
                    style={inputStyle}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                </div>
              )}

              {/* Setter Grade - only shown if not color-only mode */}
              {!isColorOnly && (
                <div style={fieldStyle}>
                  <label htmlFor="officialGrade" style={labelStyle}>
                    Setter Grade <span style={requiredStyle}>*</span>
                  </label>
                  {gradingSystem === 'font' ? (
                    <select
                      id="officialGrade"
                      name="officialGrade"
                      value={
                        formData.officialGrade ? vToFont(formData.officialGrade as VGrade) : ''
                      }
                      onChange={(e) => {
                        const fontGrade = e.target.value as FontGrade;
                        const vGrade = fontToV(fontGrade);
                        setFormData((prev) => ({ ...prev, officialGrade: vGrade }));
                      }}
                      style={selectStyle}
                      disabled={isSubmitting}
                    >
                      {FONT_GRADES.map((fontGrade) => (
                        <option key={fontGrade} value={fontGrade}>
                          {fontGrade}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      id="officialGrade"
                      name="officialGrade"
                      value={formData.officialGrade ?? 'V0'}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          officialGrade: e.target.value as VGrade,
                        }));
                      }}
                      style={selectStyle}
                      disabled={isSubmitting}
                    >
                      {V_GRADES.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  )}
                  <span style={helpTextStyle}>
                    The official difficulty grade for this route
                    {gradingSystem === 'font' && ' (displayed in Fontainebleau scale)'}
                  </span>
                </div>
              )}

              {/* Route Identification - Color selectors based on gym labeling methods */}
              {gym.colorGrades && gym.colorGrades.length > 0 && (
                <>
                  {(() => {
                    const labelingMethods = gym.labelingMethods ?? {
                      holdColors: true,
                      tagColors: false,
                      primaryMethod: 'holdColors' as const,
                      linkColors: true,
                    };
                    const bothEnabled = labelingMethods.holdColors && labelingMethods.tagColors;
                    const isLinked = bothEnabled && labelingMethods.linkColors;
                    const sortedColors = sortColorGradesByGrade(gym.colorGrades);
                    // Use separate tag colors when unlinked, otherwise same as hold colors
                    const sortedTagColors =
                      !isLinked && gym.tagColorGrades?.length
                        ? sortColorGradesByGrade(gym.tagColorGrades)
                        : sortedColors;

                    // Linked mode: single "Route Color" picker
                    if (isLinked) {
                      return (
                        <div style={fieldStyle}>
                          <label htmlFor="routeColor" style={labelStyle}>
                            Route Color <span style={requiredStyle}>*</span>
                          </label>
                          <span style={helpTextStyle}>
                            The color for this route (applies to both holds and tags)
                          </span>
                          <div style={colorGradesContainerStyle}>
                            {sortedColors.map((colorGrade) => {
                              const isSelected =
                                formData.identification.holdColor === colorGrade.displayName;
                              return (
                                <button
                                  key={colorGrade.displayName}
                                  type="button"
                                  onClick={() =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      colorGrade: colorGrade.displayName,
                                      identification: {
                                        ...prev.identification,
                                        holdColor: colorGrade.displayName,
                                        tagColor: colorGrade.displayName,
                                      },
                                    }))
                                  }
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: colorGrade.color,
                                    ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title={colorGrade.displayName}
                                >
                                  {isSelected && <span style={colorGradeCheckStyle}>&#10003;</span>}
                                </button>
                              );
                            })}
                          </div>
                          <span style={helpTextStyle}>
                            Selected: {formData.identification.holdColor || 'None selected'}
                          </span>
                        </div>
                      );
                    }

                    // Unlinked mode: separate pickers
                    const holdIsRequired = labelingMethods.primaryMethod === 'holdColors';
                    const tagIsRequired = labelingMethods.primaryMethod === 'tagColors';

                    return (
                      <>
                        {/* Hold Color - shown if holdColors is enabled */}
                        {labelingMethods.holdColors && (
                          <div style={fieldStyle}>
                            <label htmlFor="holdColor" style={labelStyle}>
                              Hold Color{' '}
                              {holdIsRequired ? (
                                <span style={requiredStyle}>*</span>
                              ) : (
                                <span style={optionalStyle}>(optional)</span>
                              )}
                            </label>
                            <span style={helpTextStyle}>The color of the holds for this route</span>
                            <div style={colorGradesContainerStyle}>
                              {/* Show "None" deselect when hold color is optional */}
                              {!holdIsRequired && (
                                <button
                                  type="button"
                                  onClick={() => clearIdentificationField('holdColor')}
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: '#e5e7eb',
                                    border: '2px dashed #9ca3af',
                                    ...(!formData.identification.holdColor
                                      ? colorGradeButtonSelectedStyle
                                      : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title="No hold color"
                                >
                                  {!formData.identification.holdColor && (
                                    <span style={colorGradeCheckStyle}>&#10003;</span>
                                  )}
                                </button>
                              )}
                              {sortedColors.map((colorGrade) => {
                                const isSelected =
                                  formData.identification.holdColor === colorGrade.displayName;
                                return (
                                  <button
                                    key={colorGrade.displayName}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        colorGrade: colorGrade.displayName,
                                        identification: {
                                          ...prev.identification,
                                          holdColor: colorGrade.displayName,
                                        },
                                      }))
                                    }
                                    style={{
                                      ...colorGradeButtonStyle,
                                      backgroundColor: colorGrade.color,
                                      ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                    }}
                                    disabled={isSubmitting}
                                    title={colorGrade.displayName}
                                  >
                                    {isSelected && (
                                      <span style={colorGradeCheckStyle}>&#10003;</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <span style={helpTextStyle}>
                              Selected: {formData.identification.holdColor || 'None'}
                            </span>
                          </div>
                        )}

                        {/* Tag/Tape Color - shown if tagColors is enabled */}
                        {labelingMethods.tagColors && (
                          <div style={fieldStyle}>
                            <label htmlFor="tagColor" style={labelStyle}>
                              Tag/Tape Color{' '}
                              {tagIsRequired ? (
                                <span style={requiredStyle}>*</span>
                              ) : (
                                <span style={optionalStyle}>(optional)</span>
                              )}
                            </label>
                            <span style={helpTextStyle}>
                              Color of tape or tags marking the route
                            </span>
                            <div style={colorGradesContainerStyle}>
                              {/* Show "None" deselect when tag color is optional */}
                              {!tagIsRequired && (
                                <button
                                  type="button"
                                  onClick={() => clearIdentificationField('tagColor')}
                                  style={{
                                    ...colorGradeButtonStyle,
                                    backgroundColor: '#e5e7eb',
                                    border: '2px dashed #9ca3af',
                                    ...(!formData.identification.tagColor
                                      ? colorGradeButtonSelectedStyle
                                      : {}),
                                  }}
                                  disabled={isSubmitting}
                                  title="No tags/tape"
                                >
                                  {!formData.identification.tagColor && (
                                    <span style={colorGradeCheckStyle}>&#10003;</span>
                                  )}
                                </button>
                              )}
                              {sortedTagColors.map((colorGrade) => {
                                const isSelected =
                                  formData.identification.tagColor === colorGrade.displayName;
                                return (
                                  <button
                                    key={colorGrade.displayName}
                                    type="button"
                                    onClick={() =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        identification: {
                                          ...prev.identification,
                                          tagColor: colorGrade.displayName,
                                        },
                                      }))
                                    }
                                    style={{
                                      ...colorGradeButtonStyle,
                                      backgroundColor: colorGrade.color,
                                      ...(isSelected ? colorGradeButtonSelectedStyle : {}),
                                    }}
                                    disabled={isSubmitting}
                                    title={colorGrade.displayName}
                                  >
                                    {isSelected && (
                                      <span style={colorGradeCheckStyle}>&#10003;</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <span style={helpTextStyle}>
                              Selected: {formData.identification.tagColor || 'None'}
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* Position on Wall */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Position in Picture <span style={optionalStyle}>(optional)</span>
                </label>
                <span style={helpTextStyle}>
                  For routes that share the same color(s) in the same picture
                </span>
                <div style={positionToggleContainerStyle}>
                  {(['left', 'center', 'right'] as const).map((pos) => {
                    const isSelected = formData.position === pos;
                    const label = pos === 'left' ? 'Left' : pos === 'center' ? 'Center' : 'Right';
                    return (
                      <button
                        key={pos}
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            position: prev.position === pos ? null : pos,
                          }))
                        }
                        style={{
                          ...positionButtonStyle,
                          ...(isSelected ? positionButtonSelectedStyle : {}),
                        }}
                        disabled={isSubmitting}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Route Attributes */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Route Attributes <span style={optionalStyle}>(optional)</span>
                </label>
                <span style={helpTextStyle}>Select attributes that describe this route</span>
                {ROUTE_STYLE_CATEGORIES.map((category) => (
                  <div key={category.key} style={{ marginBottom: '12px' }}>
                    <span style={categoryLabelStyle}>{category.label}</span>
                    <div style={stylesContainerStyle}>
                      {category.styles.map((styleInfo) => {
                        const isSelected = formData.styles.includes(styleInfo.id);
                        return (
                          <button
                            key={styleInfo.id}
                            type="button"
                            onClick={() => handleStyleToggle(styleInfo.id)}
                            style={{
                              ...styleChipStyle,
                              ...(isSelected ? styleChipSelectedStyle : {}),
                            }}
                            disabled={isSubmitting}
                            title={styleInfo.description}
                          >
                            {styleInfo.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Description - Hidden for now
              <div style={fieldStyle}>
                <label htmlFor="description" style={labelStyle}>
                  Description <span style={optionalStyle}>(optional)</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="General notes about the route..."
                  style={textareaStyle}
                  disabled={isSubmitting}
                  rows={3}
                  maxLength={1000}
                />
                <div style={charCountStyle}>{formData.description.length}/1000</div>
              </div>
              */}

              {/* Setters - Multi-setter selection */}
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Setters <span style={requiredStyle}>*</span>
                </label>
                <span style={helpTextStyle}>Select who set this route. You are pre-selected.</span>
                {settersLoading ? (
                  <p style={loadingTextStyle}>Loading setters...</p>
                ) : (
                  <SetterChipsInput
                    availableSetters={availableSetters.map(setterInfoToRouteSetter)}
                    selectedSetters={selectedSetters}
                    onChange={handleSetterChange}
                    currentUserId={user.uid}
                    currentUserName={userProfile?.displayName ?? user.displayName ?? 'Unknown'}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {/* Set Date */}
              <div style={fieldStyle}>
                <label htmlFor="setDate" style={labelStyle}>
                  Set Date
                </label>
                <input
                  id="setDate"
                  name="setDate"
                  type="date"
                  value={formData.setDate}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={isSubmitting}
                />
                <span style={helpTextStyle}>When this route was set (defaults to today)</span>
              </div>
            </div>
          </section>
        )}

        {/* Submit error */}
        {submitError && <div style={submitErrorStyle}>{submitError}</div>}
      </div>

      {/* Sticky footer with action buttons */}
      <footer style={stickyFooterStyle}>
        <div style={footerButtonsStyle}>
          {/* Save as Draft button */}
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSubmitDraft}
            style={{
              ...draftButtonStyle,
              ...(canSubmitDraft ? {} : disabledButtonStyle),
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save as Draft'}
          </button>

          {/* Publish button */}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={!canSubmitPublish}
            style={{
              ...createButtonStyle,
              ...(canSubmitPublish ? {} : disabledButtonStyle),
            }}
          >
            {isSubmitting ? 'Creating...' : 'Publish Route'}
          </button>
        </div>
      </footer>
    </main>
  );
};

/**
 * Component styles - using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
  display: 'flex',
  flexDirection: 'column',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
  padding: '20px',
  textAlign: 'center',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorSubtextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '24px',
};

const actionLinkStyle: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 500,
};

const headerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderBottom: '1px solid var(--color-border)',
  padding: '12px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const backButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  textDecoration: 'none',
  marginLeft: '-6px',
};

const backArrowStyle: CSSProperties = {
  fontSize: '1.25rem',
};

const pageTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
  lineHeight: 1.2,
};

const headerSubtitleStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const navStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderBottom: '1px solid var(--color-border)',
  overflowX: 'auto',
};

const navContentStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  display: 'flex',
  gap: '4px',
  padding: '0 20px',
};

const navButtonStyle: CSSProperties = {
  padding: '16px 20px',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottomWidth: '3px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'transparent',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flex: 1,
  justifyContent: 'center',
};

const navButtonActiveStyle: CSSProperties = {
  borderBottomColor: 'var(--color-primary)',
  color: 'var(--color-primary)',
};

const completionDotStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-success)',
  display: 'inline-block',
};

const contentStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  padding: '20px',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
};

const sectionStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  padding: '24px',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginTop: 0,
  marginBottom: '8px',
};

const sectionDescStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '20px',
};

const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const requiredStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const optionalStyle: CSSProperties = {
  fontWeight: 400,
  color: 'var(--color-text-muted)',
};

const inputStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  outline: 'none',
  transition: 'border-color 0.2s',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const selectStyle: CSSProperties = {
  padding: '12px 16px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  cursor: 'pointer',
};

const helpTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const colorGradesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
};

const colorGradeButtonStyle: CSSProperties = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  borderWidth: '2px',
  borderStyle: 'solid',
  // Subtle border visible in both light and dark modes
  borderColor: 'var(--color-border)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s, border-color 0.2s',
};

const colorGradeButtonSelectedStyle: CSSProperties = {
  borderColor: 'var(--color-primary)',
  transform: 'scale(1.1)',
};

const colorGradeCheckStyle: CSSProperties = {
  color: 'white',
  fontSize: '1.25rem',
  fontWeight: 700,
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};

const categoryLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '6px',
  marginTop: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const stylesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '4px',
};

const styleChipStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--color-border)',
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const styleChipSelectedStyle: CSSProperties = {
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  borderColor: 'var(--color-primary)',
};

const mapPlacementContainerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'var(--color-bg-primary)',
};

const mapPlacementButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '14px 24px',
  fontSize: '1rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px dashed var(--color-border)',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const mapPlacementIconStyle: CSSProperties = {
  fontSize: '1.25rem',
};

const mapPositionTextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
};

const submitErrorStyle: CSSProperties = {
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-badge-warning-text)',
  fontSize: '0.875rem',
  marginTop: '16px',
};

const stickyFooterStyle: CSSProperties = {
  position: 'sticky',
  bottom: 0,
  backgroundColor: 'var(--color-bg-primary)',
  borderTop: '1px solid var(--color-border)',
  padding: '16px 20px',
};

const footerButtonsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  maxWidth: '560px',
  margin: '0 auto',
};

const draftButtonStyle: CSSProperties = {
  flex: 1,
  padding: '16px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const createButtonStyle: CSSProperties = {
  flex: 1,
  padding: '16px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
  opacity: 0.6,
};

const positionToggleContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const positionButtonStyle: CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  textAlign: 'center',
  outline: 'none',
};

const positionButtonSelectedStyle: CSSProperties = {
  color: 'white',
  backgroundColor: 'var(--color-primary)',
};

export default CreateRoutePage;
